// src/utils/mockDataLeave.ts
// ─────────────────────────────────────────────────────────────────
// PHASE 1 — LEAVE MANAGEMENT DATA LAYER
// ─────────────────────────────────────────────────────────────────
// Module-level stores that will be replaced by real API calls
// when the Phase 1 backend is in place. All exports retain the
// same function signatures so no UI code needs to change.
//
// Leave types supported:
//   ANNUAL_LEAVE       — accrues at 4 weeks / year (NES entitlement)
//   PERSONAL_LEAVE     — accrues at 10 days / year (sick + carer)
//   LONG_SERVICE_LEAVE — portable via CIPQ; accrues at 8.67 wks per 10 yrs
//   TOIL               — Time Off In Lieu; accrued from OT election
//   WORKERS_COMP       — WorkCover QLD; no accrual, status flag only
//   TAFE_ATTENDANCE    — Apprentice block/day release; employer paid, not leave
//   PUBLIC_HOLIDAY     — system-generated; not a leave type, shown for context
// ─────────────────────────────────────────────────────────────────

export type LeaveType =
  | 'ANNUAL_LEAVE'
  | 'PERSONAL_LEAVE'
  | 'LONG_SERVICE_LEAVE'
  | 'TOIL'
  | 'WORKERS_COMP'
  | 'TAFE_ATTENDANCE'
  | 'UNPAID_LEAVE'

export type LeaveStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED' | 'TAKEN'
export type LeaveTransactionType = 'ACCRUAL' | 'DEDUCTION' | 'ADJUSTMENT' | 'OPENING_BALANCE'

export const LEAVE_TYPE_META: Record<LeaveType, {
  label: string; bg: string; text: string; border: string
  accrues: boolean; paidAtOrdinary: boolean; description: string
}> = {
  ANNUAL_LEAVE:      { label: 'Annual leave',         bg: 'bg-blue-100',   text: 'text-blue-700',   border: 'border-blue-300',   accrues: true,  paidAtOrdinary: true,  description: 'NES entitlement — 4 weeks per year' },
  PERSONAL_LEAVE:    { label: 'Personal / sick leave', bg: 'bg-red-100',    text: 'text-red-700',    border: 'border-red-300',    accrues: true,  paidAtOrdinary: true,  description: 'NES entitlement — 10 days per year (sick + carer)' },
  LONG_SERVICE_LEAVE:{ label: 'Long service leave',    bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-300', accrues: true,  paidAtOrdinary: true,  description: 'CIPQ portable — 8.67 weeks per 10 years service' },
  TOIL:              { label: 'TOIL',                  bg: 'bg-teal-100',   text: 'text-teal-700',   border: 'border-teal-300',   accrues: true,  paidAtOrdinary: false, description: 'Time Off In Lieu — accrued from elected OT' },
  WORKERS_COMP:      { label: "Workers' comp",         bg: 'bg-amber-100',  text: 'text-amber-700',  border: 'border-amber-300',  accrues: false, paidAtOrdinary: false, description: 'WorkCover QLD — employer pays, insurer reimburses' },
  TAFE_ATTENDANCE:   { label: 'TAFE attendance',       bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-300', accrues: false, paidAtOrdinary: true,  description: 'Apprentice block/day release — ordinary rate, not leave' },
  UNPAID_LEAVE:      { label: 'Unpaid leave',          bg: 'bg-gray-100',   text: 'text-gray-600',   border: 'border-gray-300',   accrues: false, paidAtOrdinary: false, description: 'Approved absence without pay' },
}

// ── Leave balance per employee ────────────────────────────────────
export interface LeaveBalance {
  employeeId:          string
  annualLeave:         number   // hours
  personalLeave:       number   // hours
  longServiceLeave:    number   // hours (CIPQ managed but tracked here)
  toil:                number   // hours
  lastAccrualDate:     string   // ISO date of most recent accrual run
  lastUpdatedAt:       string
}

// ── Leave request ─────────────────────────────────────────────────
export interface LeaveRequest {
  id:               string
  employeeId:       string
  employeeName:     string
  leaveType:        LeaveType
  startDate:        string   // ISO date
  endDate:          string   // ISO date
  workingDays:      number   // calculated from date range excl. weekends/PH
  totalHours:       number
  reason:           string
  status:           LeaveStatus
  submittedAt:      string
  reviewedByName:   string | null
  reviewedAt:       string | null
  rejectedReason:   string | null
  // Workers comp specific
  wcClaimNumber:    string | null
  wcInjuryDate:     string | null
  wcInsurer:        string | null
  // TAFE specific
  tafeBlockRef:     string | null   // links to TAFEBlock.id
  tafeInstitution:  string | null
}

// ── Leave transaction (audit trail) ──────────────────────────────
export interface LeaveTransaction {
  id:             string
  employeeId:     string
  leaveType:      LeaveType
  transactionType:LeaveTransactionType
  hours:          number      // positive = credit, negative = debit
  description:    string
  referenceId:    string | null  // leaveRequestId or payroll period
  createdAt:      string
  createdByName:  string
  balanceAfter:   number   // running balance snapshot
}

// ── TAFE attendance block ─────────────────────────────────────────
export interface TAFEBlock {
  id:              string
  employeeId:      string
  employeeName:    string
  year:            number
  blockNumber:     number    // 1 = Block 1 of the year, etc.
  releaseType:     'BLOCK' | 'DAY_RELEASE'
  startDate:       string
  endDate:         string
  institution:     string   // e.g. 'TAFE Queensland — South Bank'
  course:          string   // e.g. 'Certificate III Electrotechnology (UEE30820)'
  units:           string[] // unit codes covered in this block
  status:          'SCHEDULED' | 'CONFIRMED' | 'COMPLETED' | 'DEFERRED'
  hoursPerDay:     number   // typically 6h for TAFE
  totalDays:       number
  totalHours:      number   // calculated
  notes:           string
}

// ─────────────────────────────────────────────────────────────────
// MODULE-LEVEL STORES
// ─────────────────────────────────────────────────────────────────

const _leaveBalances: LeaveBalance[] = [
  // Seed balances for existing mock employees
  { employeeId:'e1', annualLeave:120, personalLeave:64,  longServiceLeave:0,  toil:8,   lastAccrualDate:'2026-06-30', lastUpdatedAt: new Date().toISOString() },
  { employeeId:'e2', annualLeave:80,  personalLeave:48,  longServiceLeave:0,  toil:0,   lastAccrualDate:'2026-06-30', lastUpdatedAt: new Date().toISOString() },
  { employeeId:'e3', annualLeave:56,  personalLeave:32,  longServiceLeave:0,  toil:16,  lastAccrualDate:'2026-06-30', lastUpdatedAt: new Date().toISOString() },
  { employeeId:'e4', annualLeave:96,  personalLeave:56,  longServiceLeave:0,  toil:0,   lastAccrualDate:'2026-06-30', lastUpdatedAt: new Date().toISOString() },
  { employeeId:'e5', annualLeave:112, personalLeave:72,  longServiceLeave:40, toil:0,   lastAccrualDate:'2026-06-30', lastUpdatedAt: new Date().toISOString() },
  { employeeId:'e6', annualLeave:64,  personalLeave:40,  longServiceLeave:0,  toil:4,   lastAccrualDate:'2026-06-30', lastUpdatedAt: new Date().toISOString() },
  { employeeId:'e7', annualLeave:144, personalLeave:80,  longServiceLeave:80, toil:0,   lastAccrualDate:'2026-06-30', lastUpdatedAt: new Date().toISOString() },
  { employeeId:'e8', annualLeave:24,  personalLeave:16,  longServiceLeave:0,  toil:0,   lastAccrualDate:'2026-06-30', lastUpdatedAt: new Date().toISOString() },
  { employeeId:'e9', annualLeave:88,  personalLeave:48,  longServiceLeave:0,  toil:8,   lastAccrualDate:'2026-06-30', lastUpdatedAt: new Date().toISOString() },
]

const _leaveRequests: LeaveRequest[] = []
const _leaveTransactions: LeaveTransaction[] = []
const _tafeBlocks: TAFEBlock[] = [
  // Seed TAFE blocks for e8 (apprentice)
  {
    id: 'tb001', employeeId: 'e8', employeeName: 'Jake Thornton',
    year: 2026, blockNumber: 1, releaseType: 'BLOCK',
    startDate: '2026-07-28', endDate: '2026-08-08',
    institution: 'TAFE Queensland — South Bank',
    course: 'Certificate III Electrotechnology Electrician (UEE30820)',
    units: ['UEENEEE101A', 'UEENEEE102A', 'UEENEEG101A'],
    status: 'SCHEDULED', hoursPerDay: 6, totalDays: 10, totalHours: 60,
    notes: 'Block 1 of 4 for 2026 — introductory electrical theory',
  },
  {
    id: 'tb002', employeeId: 'e8', employeeName: 'Jake Thornton',
    year: 2026, blockNumber: 2, releaseType: 'BLOCK',
    startDate: '2026-10-19', endDate: '2026-10-30',
    institution: 'TAFE Queensland — South Bank',
    course: 'Certificate III Electrotechnology Electrician (UEE30820)',
    units: ['UEENEEE104A', 'UEENEEG002A'],
    status: 'SCHEDULED', hoursPerDay: 6, totalDays: 10, totalHours: 60,
    notes: 'Block 2 of 4 for 2026',
  },
]

// ─────────────────────────────────────────────────────────────────
// LEAVE BALANCE FUNCTIONS
// ─────────────────────────────────────────────────────────────────

export function getLeaveBalance(employeeId: string): LeaveBalance | null {
  return _leaveBalances.find(b => b.employeeId === employeeId) ?? null
}

export function getAllLeaveBalances(): LeaveBalance[] {
  return [..._leaveBalances]
}

/** Deduct hours from a leave balance when leave is approved.
 *  Returns false if insufficient balance. */
export function deductLeave(
  employeeId: string,
  leaveType: 'annualLeave' | 'personalLeave' | 'longServiceLeave' | 'toil',
  hours: number,
  deductedByName: string,
  requestId: string,
): boolean {
  const bal = _leaveBalances.find(b => b.employeeId === employeeId)
  if (!bal) return false
  if (bal[leaveType] < hours) return false
  bal[leaveType]     = +(bal[leaveType] - hours).toFixed(2)
  bal.lastUpdatedAt  = new Date().toISOString()
  _leaveTransactions.push({
    id:              `lt-${Date.now()}`,
    employeeId,
    leaveType:       leaveType === 'annualLeave' ? 'ANNUAL_LEAVE' :
                     leaveType === 'personalLeave' ? 'PERSONAL_LEAVE' :
                     leaveType === 'longServiceLeave' ? 'LONG_SERVICE_LEAVE' : 'TOIL',
    transactionType: 'DEDUCTION',
    hours:           -hours,
    description:     `Leave taken — approved`,
    referenceId:     requestId,
    createdAt:       new Date().toISOString(),
    createdByName:   deductedByName,
    balanceAfter:    bal[leaveType],
  })
  return true
}

/** Credit TOIL hours when an employee elects OT as TOIL */
export function accrueToil(employeeId: string, hours: number, payPeriod: string, createdByName: string): void {
  const bal = _leaveBalances.find(b => b.employeeId === employeeId)
  if (!bal) return
  bal.toil          = +(bal.toil + hours).toFixed(2)
  bal.lastUpdatedAt = new Date().toISOString()
  _leaveTransactions.push({
    id:              `lt-${Date.now()}`,
    employeeId,
    leaveType:       'TOIL',
    transactionType: 'ACCRUAL',
    hours,
    description:     `TOIL accrued from pay period ${payPeriod}`,
    referenceId:     payPeriod,
    createdAt:       new Date().toISOString(),
    createdByName,
    balanceAfter:    bal.toil,
  })
}

/** Manual balance adjustment (admin correction) */
export function adjustLeaveBalance(
  employeeId: string,
  leaveType: 'annualLeave' | 'personalLeave' | 'longServiceLeave' | 'toil',
  hours: number,   // positive = credit, negative = debit
  reason: string,
  adjustedByName: string,
): void {
  const bal = _leaveBalances.find(b => b.employeeId === employeeId)
  if (!bal) return
  bal[leaveType]    = Math.max(0, +(bal[leaveType] + hours).toFixed(2))
  bal.lastUpdatedAt = new Date().toISOString()
  _leaveTransactions.push({
    id:              `lt-${Date.now()}`,
    employeeId,
    leaveType:       leaveType === 'annualLeave' ? 'ANNUAL_LEAVE' : leaveType === 'personalLeave' ? 'PERSONAL_LEAVE' : leaveType === 'longServiceLeave' ? 'LONG_SERVICE_LEAVE' : 'TOIL',
    transactionType: 'ADJUSTMENT',
    hours,
    description:     `Balance adjustment — ${reason}`,
    referenceId:     null,
    createdAt:       new Date().toISOString(),
    createdByName:   adjustedByName,
    balanceAfter:    bal[leaveType],
  })
}

// ─────────────────────────────────────────────────────────────────
// LEAVE REQUEST FUNCTIONS
// ─────────────────────────────────────────────────────────────────

export function getLeaveRequests(employeeId?: string): LeaveRequest[] {
  return employeeId
    ? _leaveRequests.filter(r => r.employeeId === employeeId)
    : [..._leaveRequests]
}

export function getPendingLeaveRequests(): LeaveRequest[] {
  return _leaveRequests.filter(r => r.status === 'PENDING')
}

export function submitLeaveRequest(req: Omit<LeaveRequest, 'id' | 'submittedAt' | 'status' | 'reviewedByName' | 'reviewedAt' | 'rejectedReason'>): LeaveRequest {
  const newReq: LeaveRequest = {
    ...req,
    id:              `lr-${Date.now()}`,
    status:          'PENDING',
    submittedAt:     new Date().toISOString(),
    reviewedByName:  null,
    reviewedAt:      null,
    rejectedReason:  null,
  }
  _leaveRequests.push(newReq)
  return newReq
}

export function approveLeaveRequest(requestId: string, reviewedByName: string): boolean {
  const req = _leaveRequests.find(r => r.id === requestId)
  if (!req || req.status !== 'PENDING') return false
  req.status          = 'APPROVED'
  req.reviewedByName  = reviewedByName
  req.reviewedAt      = new Date().toISOString()
  // Deduct from balance
  if (req.leaveType === 'ANNUAL_LEAVE')       deductLeave(req.employeeId, 'annualLeave',       req.totalHours, reviewedByName, requestId)
  if (req.leaveType === 'PERSONAL_LEAVE')     deductLeave(req.employeeId, 'personalLeave',     req.totalHours, reviewedByName, requestId)
  if (req.leaveType === 'LONG_SERVICE_LEAVE') deductLeave(req.employeeId, 'longServiceLeave',  req.totalHours, reviewedByName, requestId)
  if (req.leaveType === 'TOIL')               deductLeave(req.employeeId, 'toil',              req.totalHours, reviewedByName, requestId)
  return true
}

export function rejectLeaveRequest(requestId: string, reviewedByName: string, reason: string): boolean {
  const req = _leaveRequests.find(r => r.id === requestId)
  if (!req || req.status !== 'PENDING') return false
  req.status          = 'REJECTED'
  req.reviewedByName  = reviewedByName
  req.reviewedAt      = new Date().toISOString()
  req.rejectedReason  = reason
  return true
}

export function getLeaveTransactions(employeeId: string): LeaveTransaction[] {
  return _leaveTransactions.filter(t => t.employeeId === employeeId)
}

// ─────────────────────────────────────────────────────────────────
// TAFE BLOCK FUNCTIONS
// ─────────────────────────────────────────────────────────────────

export function getTAFEBlocks(employeeId?: string): TAFEBlock[] {
  return employeeId
    ? _tafeBlocks.filter(b => b.employeeId === employeeId)
    : [..._tafeBlocks]
}

/** Check if a given date is a TAFE attendance day for an employee */
export function isTAFEDate(employeeId: string, dateStr: string): boolean {
  const d = new Date(dateStr + 'T00:00:00')
  return _tafeBlocks.some(b =>
    b.employeeId === employeeId &&
    b.status !== 'DEFERRED' &&
    new Date(b.startDate + 'T00:00:00') <= d &&
    new Date(b.endDate + 'T23:59:59')   >= d
  )
}

/** Get TAFE block active on a specific date for an employee */
export function getTAFEBlockForDate(employeeId: string, dateStr: string): TAFEBlock | undefined {
  const d = new Date(dateStr + 'T00:00:00')
  return _tafeBlocks.find(b =>
    b.employeeId === employeeId &&
    b.status !== 'DEFERRED' &&
    new Date(b.startDate + 'T00:00:00') <= d &&
    new Date(b.endDate + 'T23:59:59')   >= d
  )
}

export function addTAFEBlock(block: Omit<TAFEBlock, 'id' | 'totalHours'>): TAFEBlock {
  const newBlock: TAFEBlock = {
    ...block,
    id: `tb-${Date.now()}`,
    totalHours: block.totalDays * block.hoursPerDay,
  }
  _tafeBlocks.push(newBlock)
  return newBlock
}

export function updateTAFEBlockStatus(blockId: string, status: TAFEBlock['status']): void {
  const b = _tafeBlocks.find(x => x.id === blockId)
  if (b) b.status = status
}

// ─────────────────────────────────────────────────────────────────
// LEAVE ACCRUAL HELPERS (used by payroll summary)
// ─────────────────────────────────────────────────────────────────

/** Annual leave accrual per hour worked (NES: 4 weeks / 52 weeks = 0.07692 weeks/week) */
export const AL_ACCRUAL_RATE_PER_HOUR = 4 * 38 / (52 * 38)   // ≈ 0.07692 hours AL per hour worked

/** Personal leave accrual per hour worked (NES: 10 days / year) */
export const PL_ACCRUAL_RATE_PER_HOUR = 10 * 7.6 / (52 * 38) // ≈ 0.03846 hours PL per hour worked

/** Format leave balance in hours and days for display */
export function formatLeaveBalance(hours: number, hoursPerDay = 7.6): string {
  const days = (hours / hoursPerDay).toFixed(1)
  return `${hours.toFixed(1)}h (${days} days)`
}
